import { useTPAxios } from "@/api/useTPAxios.ts";
import { queryOptions, useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { AxiosInstance, AxiosResponse } from "axios";
import { Product } from "./product.model";
import { useBrandConfig } from "@/utils/useBrandConfig";
import { Sustainability } from "./sustainability.model";
import { ProductHistory } from "./product-history.model";
import { RequestProps } from "@/utils/response.utils";
import i18n from "@/utils/i18n.utils";
import { ApiError } from "@/utils/apiError.model";
import { Marketplace, Partner } from "./partner.model";

const listProducts = async (
  brandName: string,
  TPAxios: AxiosInstance
): Promise<Product[]> => {
  const response = await TPAxios.get<AxiosResponse<Product[]>>(
    `/customers/me/products?brand=${brandName}`,
  );

  return response.data.data;
}

const downloadCertificate = async (
  id: string,
  TPAxios: AxiosInstance,
): Promise<ArrayBuffer> => {
  const response = await TPAxios.get(
    `/certificates/${id}.pdf`,
    {
      responseType: "arraybuffer",
    },
  );

  return response.data as unknown as ArrayBuffer;
};

const getSustainability = async (
  useTPAxios: AxiosInstance,
  id: string
): Promise<Sustainability> => {
  const response = await useTPAxios.get<AxiosResponse<Sustainability>>(
    `/categories/${id}/sustainability`,
  );

  return response.data.data;
};

const listMarketplaces = async (
  useTPAxios: AxiosInstance,
  id: string
): Promise<Marketplace[]> => {
  const response = await useTPAxios.get<AxiosResponse<Marketplace[]>>(
    `/brands/${id}/marketplaces`,
  );

  return response.data.data;
}

const listPartners = async (
  useTPAxios: AxiosInstance,
  id: string
): Promise<Partner[]> => {
  const response = await useTPAxios.get<AxiosResponse<PaginatedResult<Partner>>>(
    `/brands/${id}/partners?page=1&limit=1000`,
  );

  return response.data.data.docs;
}

const listProductHistory = async (
  TPAxios: AxiosInstance,
  categoryId: string,
): Promise<ProductHistory[]> => {
  const response = await TPAxios.get<AxiosResponse<ProductHistory[]>>(
    `/categories/${categoryId}/history`,
  );

  return response.data.data;
}

export type GiftProductProps = {
  reference: string;
  receiver: {
    email: string;
    firstName: string;
    lastName: string;
  };
  message?: string;
  images?: string[];
}

export type SellProductsProps = {
  reference: string;
  owner: {
    email: string;
    firstName: string;
    lastName: string;
  };
}

export type ResaleProductsProps = {
  reference: string;
  secondhand: string;
}

export type SendRepairRequestProps = {
  comment?: string;
  reason?: string;
  product: string;
  partner?: string;
}

export type LockProductProps = {
  id: string;
  reason: string;
  comment?: string;
}

const base64ToFile = (base64: string, filename: string, mimeType: string): File => {
  const byteString = atob(base64.split(',')[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new File([ab], filename, { type: mimeType });
}

const sellProduct = async (
  body: SellProductsProps,
  TPAxios: AxiosInstance,
): Promise<void> => {
  await TPAxios.post<void>(
    '/transactions/resale/direct',
    body,
  );

  return;
}

const resaleProduct = async (
  body: ResaleProductsProps,
  TPAxios: AxiosInstance,
): Promise<void> => {
  await TPAxios.post<void>(
    '/transactions/resale/',
    body,
  );

  return;
}

const sendRepairRequest = async (
  body: SendRepairRequestProps,
  TPAxios: AxiosInstance,
): Promise<void> => {
  await TPAxios.post<void>(
    '/repairs',
    body
  );

  return;
}

const lockProduct = async (
  params: LockProductProps,
  TPAxios: AxiosInstance,
) => {
  const { id, comment, reason } = params;
  await TPAxios.put(
    `/products/${id}/report`,
    { reason, comment }
  );

  return;
}

const unlockProduct = async (
  id: string,
  TPAxios: AxiosInstance,
) => {
  await TPAxios.post(
    `/products/${id}/unreport`,
  );

  return;
}

const unlockGift = async (
  id: string,
  TPAxios: AxiosInstance,
) => {
  await TPAxios.put(
    `/gifts/${id}/`,
    { seen: true }
  );

  return;
}

const unlockResale = async (
  id: string,
  TPAxios: AxiosInstance,
) => {
  await TPAxios.put(
    `/resales/${id}/`,
    { seen: true }
  );

  return;
}

export const useSellProduct = (
  props: RequestProps
) => {
  const { onSuccess, onError } = props;
  const TPAxios = useTPAxios();

  return useMutation({
    mutationFn: (body: SellProductsProps) => sellProduct(body, TPAxios),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      const err = error as ApiError;
      const errorCode = err.response?.data.errorCode;
      onError(i18n.t(`errors.${errorCode}`));
    }
  })
}

const giftProduct = async (
  body: GiftProductProps,
  TPAxios: AxiosInstance,
): Promise<void> => {

  // upload images
  if (body.images && body.images.length > 0) {
    const files = body.images.map((image, index) => base64ToFile(image, `image${index}.png`, 'image/png'))

    const formData = new FormData();

    files.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });

    const uploadResponse = await TPAxios.post<{ data: string[] }>(
      '/upload/multiple',
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    body.images = Object.values(uploadResponse.data.data);
  }

  await TPAxios.post<void>(
    '/transactions/gift',
    body,
  );

  return;
}

export const useGiftProduct = (
  props: RequestProps
) => {
  const { onSuccess, onError } = props;
  const TPAxios = useTPAxios();

  return useMutation({
    mutationFn: (body: GiftProductProps) => giftProduct(body, TPAxios),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      const err = error as ApiError;
      const errorCode = err.response?.data.errorCode;
      onError(i18n.t(`errors.${errorCode}`));
    }
  })
}

export const useListProducts = () => {
  const TPAxios = useTPAxios();
  const { name } = useBrandConfig();

  const options = queryOptions({
    queryKey: ['products'],
    queryFn: async () => await listProducts(name, TPAxios),
  });

  return useSuspenseQuery(options);
}

export const useListMarketplaces = (id: string) => {
  const TPAxios = useTPAxios();

  return useQuery({
    queryKey: ['marketplaces', id],
    queryFn: async () => await listMarketplaces(TPAxios, id!),
    enabled: !!id,
  });
}

export const useListPartners = (id: string) => {
  const TPAxios = useTPAxios();

  return useQuery({
    queryKey: ['partners', id],
    queryFn: async () => await listPartners(TPAxios, id!),
    enabled: !!id,
  });
}

export const useDownloadCertificate = (
  props: RequestProps
) => {
  const { onSuccess, onError } = props;
  const TPAxios = useTPAxios();

  return useMutation({
    mutationFn: (id: string) => downloadCertificate(id, TPAxios),
    onSuccess: (data) => {
      const fileHandler = new FileHandler();
      const url = window.URL.createObjectURL(new Blob([data]));

      fileHandler.download({
        url,
        fileName: `${Date.now()}_certificate.pdf`,
      });

      onSuccess();
    },
    onError: (error) => {
      const err = error as ApiError;
      const errorCode = err.response?.data.errorCode;
      onError(i18n.t(`errors.${errorCode}`));
    }
  })
}

export const useResaleProduct = (
  props: RequestProps
) => {
  const { onSuccess, onError } = props;
  const TPAxios = useTPAxios();

  return useMutation({
    mutationFn: (body: ResaleProductsProps) => resaleProduct(body, TPAxios),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      const err = error as ApiError;
      const errorCode = err.response?.data.errorCode;
      onError(i18n.t(`errors.${errorCode}`));
    }
  })
}

export const useLockProduct = (
  props: RequestProps
) => {
  const { onSuccess, onError } = props;
  const TPAxios = useTPAxios();

  return useMutation({
    mutationFn: (body: LockProductProps) => lockProduct(body, TPAxios),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      const err = error as ApiError;
      const errorCode = err.response?.data.errorCode;
      onError(i18n.t(`errors.${errorCode}`));
    }
  })
}

export const useUnlockProduct = (
  props: RequestProps
) => {
  const { onSuccess, onError } = props;
  const TPAxios = useTPAxios();

  return useMutation({
    mutationFn: (id: string) => unlockProduct(id, TPAxios),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      const err = error as ApiError;
      const errorCode = err.response?.data.errorCode;
      onError(i18n.t(`errors.${errorCode}`));
    }
  })
}

export const useUnlockGift = (
  props: RequestProps
) => {
  const { onSuccess, onError } = props;
  const TPAxios = useTPAxios();

  return useMutation({
    mutationFn: (id: string) => unlockGift(id, TPAxios),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      const err = error as ApiError;
      const errorCode = err.response?.data.errorCode;
      onError(i18n.t(`errors.${errorCode}`));
    }
  })
}

export const useUnlockResale = (
  props: RequestProps
) => {
  const { onSuccess, onError } = props;
  const TPAxios = useTPAxios();

  return useMutation({
    mutationFn: (id: string) => unlockResale(id, TPAxios),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      const err = error as ApiError;
      const errorCode = err.response?.data.errorCode;
      onError(i18n.t(`errors.${errorCode}`));
    }
  })
}


export const useSendRepairRequest = (
  props: RequestProps
) => {
  const { onSuccess, onError } = props;
  const TPAxios = useTPAxios();

  return useMutation({
    mutationFn: (body: SendRepairRequestProps) => sendRepairRequest(body, TPAxios),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      const err = error as ApiError;
      const errorCode = err.response?.data.errorCode;
      onError(i18n.t(`errors.${errorCode}`));
    }
  })
}

export const useGetSustainability = (id: string) => {
  const TPAxios = useTPAxios();

  return useQuery({
    queryKey: ['sustainability', id],
    queryFn: async () => await getSustainability(TPAxios, id!),
    enabled: !!id,
  });
}

export const useListProductHistory = (categoryId: string) => {
  const TPAxios = useTPAxios();

  return useQuery({
    queryKey: ['productHistory', categoryId],
    queryFn: async () => await listProductHistory(TPAxios, categoryId),
    enabled: !!categoryId,
  });
}

export type PaginatedResult<T> = {
  count: number;
  docs: T[];
};

export default class FileHandler {
  private static instance: FileHandler;

  constructor() {
    if (FileHandler.instance) {
      return FileHandler.instance;
    }
    FileHandler.instance = this;
  }

  download({ url, fileName }: { url: string; fileName: string }): void {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
  }
}
